import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getFormValid } from 'utils/formUtils';
import FormLabelWithField from 'components/Core/FormLabelWithField';
import Button from 'components/Core/Button';
import Select from 'components/Core/Select';
import TextField from 'components/Core/TextField';
import NumberInputField from 'components/Core/NumberInputField';
import EmbedForm from '../../EmbeddableForm';
import EmbedLink from '../../EmbedLink';

const RoofingAreaCalcForm = ({ pathUrl, onSubmit, show, ...otherProps }) => {
  const measureTypeOptions = [
    {
      label: 'Roof Pitch',
      value: 'roofPitch',
    },
    {
      label: 'Roof Angle',
      value: 'roofAngle',
    },
  ];
  const pitchOptions = Array.from({ length: 24 }, (_, i) => ({
    label: `${i + 1}/12`,
    value: `${i + 1}/12`,
  }));

  const onCalc = ({ area, eavesStickOut, measureType, roofPitch, roofAngle, price }) => {
    let pitchFactor = 0;
    if (measureType === 'roofPitch') {
      const [rise, run] = roofPitch.split('/').map(Number);
      pitchFactor = Math.sqrt(rise ** 2 + run ** 2) / run;
    } else {
      const angleInRadians = (roofAngle * Math.PI) / 180;
      pitchFactor = 1 / Math.cos(angleInRadians);
    }

    const adjustedBaseArea = (Math.sqrt(area) + 2 * eavesStickOut) ** 2;
    const calculatedRoofArea = adjustedBaseArea * pitchFactor;
    const calculatedPrice = price ? calculatedRoofArea * price : 0;

    onSubmit && onSubmit({ estimatedArea: calculatedRoofArea, estimatedPrice: calculatedPrice });
  };

  const formik = useFormik({
    initialValues: {
      area: 2200,
      roofPitch: pitchOptions[0].value,
      measureType: measureTypeOptions[0].value,
    },
    validationSchema: Yup.object().shape({
      area: Yup.number().required('House Base Area is required'),
      roofPitch: Yup.string().required('Ratio is required'),
      eavesStickOut: Yup.number().required('Eaves Stick Out is required'),
      roofAngle: Yup.string().when('measureType', {
        is: 'roofAngle',
        then: Yup.string().required('Roof Angle is required'),
      }),
    }),
    onSubmit: (_values) => {
      onCalc(_values);
    },
  });

  const onHandleChange = (id, newValue) => {
    formik.setFieldTouched(id);
    formik.setFieldValue(id, newValue);
  };

  const formValid = getFormValid({ ...formik });

  return (
    <div className={`w-full h-full absolute top-0 ${show ? 'z-10' : 'z-0'}`}>
      <div id="roofing_area_calc_form">
        <div
          className={`transition-all duration-500 ${
            show ? 'visible opacity-100' : 'invisible opacity-0'
          }`}
          data-position="roofing pitch form"
        >
          <div className="mb-[21px]">
            <FormLabelWithField label="House Base Area (Feet)">
              <NumberInputField
                id="area"
                min={100}
                max={10000}
                values={formik.values}
                formValid={formValid}
                handleChange={onHandleChange}
              />
            </FormLabelWithField>
          </div>
          <div className="mb-[21px]">
            <FormLabelWithField label="Measurement Type">
              <div className="flex">
                <div className="w-[150px] md:w-[150px]">
                  <Select
                    id="measureType"
                    values={formik.values}
                    options={measureTypeOptions}
                    formValid={formValid}
                    handleChange={onHandleChange}
                    defaultValue={pitchOptions[0].value}
                  />
                </div>
                <div className="w-auto flex-[1]">
                  {formik.values.measureType === 'roofPitch' ? (
                    <Select
                      id="roofPitch"
                      values={formik.values}
                      options={pitchOptions}
                      formValid={formValid}
                      handleChange={onHandleChange}
                      defaultValue={pitchOptions[0].value}
                    />
                  ) : (
                    <TextField
                      showOnlyFormError
                      id="roofAngle"
                      type="number"
                      values={formik.values}
                      formValid={formValid}
                      handleChange={onHandleChange}
                      allowNegative={false}
                      placeholder="Roof Angle (degrees)"
                    />
                  )}
                </div>
              </div>
            </FormLabelWithField>
          </div>
          <div className="mb-[21px]">
            <FormLabelWithField label="Eaves Stick Out (Feet)">
              <TextField
                showOnlyFormError
                id="eavesStickOut"
                type="number"
                values={formik.values}
                formValid={formValid}
                handleChange={onHandleChange}
                allowNegative={false}
                placeholder="Eaves (ft)"
              />
            </FormLabelWithField>
          </div>
          <div className="mb-[21px]">
            <FormLabelWithField label="Price Per Square Foot (Optional)">
              <TextField
                showOnlyFormError
                id="price"
                type="number"
                values={formik.values}
                formValid={formValid}
                handleChange={onHandleChange}
                allowNegative={false}
                placeholder="$"
              />
            </FormLabelWithField>
          </div>
        </div>
      </div>
      <div className={`absolute bottom-0 w-full ${show ? 'visible' : 'invisible'}`}>
        <Button
          className="!w-full"
          type="Purple"
          shadow={false}
          onClick={() => formik.submitForm()}
        >
          Calculate
        </Button>
        <div className="form-embed-link">
          <EmbedLink
            show={!otherProps?.isEmbed}
            openCopyEmbedLinkDialog={otherProps?.openCopyEmbedLinkDialog}
            label="Add tool to your website"
            pathUrl={pathUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default EmbedForm(RoofingAreaCalcForm);

export const RoofingAreaCalcResult = ({ result }) => (
  <div className="flex flex-col items-center self-stretch gap-[10px]">
    <h4 className="text-[14px] md:text-[16px] font-black text-[var(--grey-8)] tracking-[0.7px] md:tracking-[0.8px] leading-[110%] mb-[10px]">
      RESULTS
    </h4>
    <p className="text-[17px] md:text-[20px] font-normal text-black text-center leading-[150%] mb-0">
      Estimated Roof Area:
    </p>
    <h3 className="text-[26px] md:text-[35px] font-extrabold text-black leading-[120%] tracking-[-0.78px] md:tracking-[-1.4px] mb-0">
      {result.estimatedArea?.toFixed(2)}
      &nbsp;ft²
    </h3>
    {result.estimatedPrice > 0 && (
      <>
        <p className="text-[17px] md:text-[20px] font-normal text-black text-center leading-[150%] mb-0 mt-[10px]">
          Estimated Price:
        </p>
        <h3 className="text-[26px] md:text-[35px] font-extrabold text-black leading-[120%] tracking-[-0.78px] md:tracking-[-1.4px] mb-0">
          {`$${result.estimatedPrice?.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
        </h3>
      </>
    )}
  </div>
);
